<template>
  <div
    class="voice-player"
    :class="{
      'is-playing': isPlaying,
      'is-disabled': !audioUrl
    }"
    :style="{ width: props.width }"
  >
    <div class="voice-player__inner">
      <div v-if="hasTopSlot" class="voice-player__top">
        <slot :message="message" name="top"></slot>
      </div>
      <div class="voice-player__bottom">
        <button
          class="voice-player__button"
          :disabled="!audioUrl"
          type="button"
          @click.stop="togglePlay"
        >
          <i :class="buttonIconClass"></i>
        </button>
        <span class="voice-player__progress">
          <span class="voice-player__progress-track">
            <span class="voice-player__progress-fill" :style="progressStyle"></span>
          </span>
        </span>
      </div>
    </div>
    <div v-if="hasActions" class="voice-player__actions">
      <a
        v-if="showDownload"
        class="voice-player__action"
        :class="{ 'is-disabled': !audioUrl }"
        :download="downloadFileName"
        :href="audioUrl"
        rel="noopener noreferrer"
        target="_blank"
        title="下载"
        @click.stop="handleDownloadClick"
      >
        <i class="icon-download-2-line t-iconfont"></i>
        下载
      </a>
      <el-dropdown
        v-if="showSpeed"
        placement="bottom"
        trigger="click"
        @click.native.stop
        @command="handleSpeedChange"
      >
        <button
          class="voice-player__speed"
          :class="playbackRate !== 1 && 'is-speeding'"
          type="button"
        >
          <i class="icon-flashlight-line t-iconfont"></i>
          倍速
        </button>
        <el-dropdown-menu slot="dropdown" class="voice-player__speed-menu">
          <el-dropdown-item
            v-for="speedItem in speedOptions"
            :key="speedItem.value"
            :class="{ 'is-active': speedItem.value === playbackRate }"
            :command="speedItem.value"
          >
            {{ speedItem.label }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <audio
      ref="audioRef"
      class="voice-player__audio"
      preload="metadata"
      :src="audioUrl"
      @ended="handleEnded"
      @error="handleError"
      @loadedmetadata="handleLoadedMetadata"
      @pause="handlePause"
      @play="handlePlay"
      @timeupdate="handleTimeUpdate"
    ></audio>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, useSlots, watch } from 'vue'

let activeAudio = null

const props = defineProps({
  message: {
    type: Object,
    default: () => ({
      id: '',
      url: '',
      time: ''
    })
  },
  width: {
    type: String,
    default: 'auto'
  },
  options: {
    type: Array,
    default: () => []
  }
})

const audioRef = ref(null)
const slots = useSlots()
const hasTopSlot = computed(() => Boolean(slots.top))
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const playbackRate = ref(1)
const speedOptions = [
  { label: '2.0 x', value: 2 },
  { label: '1.5 x', value: 1.5 },
  { label: '1.25 x', value: 1.25 },
  { label: '1.0 x', value: 1 },
  { label: '0.5 x', value: 0.5 }
]

const audioUrl = computed(() => props.message.url || '')
const showDownload = computed(() => props.options.includes('download'))
const showSpeed = computed(() => props.options.includes('speed'))
const hasActions = computed(() => showDownload.value || showSpeed.value)
// const currentSpeedLabel = computed(() => {
//   const speedItem = speedOptions.find((item) => item.value === playbackRate.value)

//   return speedItem ? speedItem.label : `${playbackRate.value} x`
// })
const downloadFileName = computed(() => {
  return props.message.fileName || props.message.name || `${props.message.id || 'voice'}.mp3`
})

const progress = computed(() => {
  const totalDuration = duration.value || Number(props.message.time) || 0
  if (!totalDuration) return 0

  return Math.min((currentTime.value / totalDuration) * 100, 100)
})

const progressStyle = computed(() => ({
  width: `${progress.value}%`
}))

const buttonIconClass = computed(() => {
  return isPlaying.value ? 'icon-pause-circle-line t-iconfont' : 'icon-play-circle-line t-iconfont'
})

const pauseCurrentAudio = (audioElement) => {
  if (!audioElement) return

  audioElement.pause()
}

const resetCurrentAudio = (audioElement) => {
  if (!audioElement) return

  audioElement.pause()
  if (audioElement.currentTime) {
    audioElement.currentTime = 0
  }
}

const pauseAudio = () => {
  pauseCurrentAudio(audioRef.value)
}

const stopAudio = () => {
  resetCurrentAudio(audioRef.value)
  currentTime.value = 0
}

const togglePlay = () => {
  const audioElement = audioRef.value
  if (!audioElement || !audioUrl.value) return

  if (isPlaying.value) {
    pauseAudio()
    return
  }

  if (activeAudio && activeAudio !== audioElement) {
    resetCurrentAudio(activeAudio)
  }

  activeAudio = audioElement
  audioElement.play().catch(() => {
    if (activeAudio === audioElement) {
      activeAudio = null
    }
  })
}

const handleLoadedMetadata = () => {
  const audioElement = audioRef.value
  if (!audioElement) return

  audioElement.playbackRate = playbackRate.value
  duration.value = audioElement.duration || Number(props.message.time) || 0
}

const handleTimeUpdate = () => {
  const audioElement = audioRef.value
  if (!audioElement) return

  currentTime.value = audioElement.currentTime || 0
}

const handleDownloadClick = (event) => {
  if (!audioUrl.value) {
    event.preventDefault()
  }
}

const handleSpeedChange = (speed) => {
  playbackRate.value = speed
  const audioElement = audioRef.value
  if (audioElement) {
    audioElement.playbackRate = speed
  }
}

const handlePlay = () => {
  isPlaying.value = true
}

const handlePause = () => {
  isPlaying.value = false
  if (activeAudio === audioRef.value) {
    activeAudio = null
  }
}

const resetPlayState = () => {
  isPlaying.value = false
  currentTime.value = 0
  if (activeAudio === audioRef.value) {
    activeAudio = null
  }
}

const handleEnded = () => {
  resetPlayState()
}

const handleError = () => {
  resetPlayState()
}

watch(
  () => props.message.url,
  () => {
    stopAudio()
    duration.value = 0
  }
)

onBeforeUnmount(() => {
  stopAudio()
  if (activeAudio === audioRef.value) {
    activeAudio = null
  }
})
</script>

<style lang="scss" scoped>
.voice-player {
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  // width: 124px;
  user-select: none;
  outline: none;

  &__inner {
    box-sizing: border-box;
    display: inline-flex;
    flex-direction: column;
    padding: 8px 12px;
    // background: #fff;
    border-radius: 8px;
  }

  &.is-disabled {
    opacity: 0.6;
  }

  &__top {
    display: flex;
    align-items: center;
    line-height: 20px;

    + .voice-player__bottom {
      margin-top: 8px;
    }
  }

  &__bottom {
    display: inline-flex;
    align-items: center;
  }

  &__button {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    color: #4e5969;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;

    .t-iconfont {
      font-size: 16px;
      line-height: 1;
      color: #248df5;
    }

    &:disabled {
      cursor: default;
    }
  }

  &__progress {
    display: inline-flex;
    flex: 1;
    align-items: center;
    min-width: 32px;
    margin-left: 8px;
  }

  &__actions {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    // margin-top: 12px;
    padding-top: 12px;
    background: #fff;
  }

  &__action,
  &__speed {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 8px;
    margin-right: 4px;
    font-size: 12px;
    line-height: 24px;
    color: #969799;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 8px;
    outline: none;

    // + .voice-player__speed,
    // + .el-dropdown {
    //   margin-left: 12px;
    // }

    &.is-disabled {
      cursor: default;
    }

    .t-iconfont {
      margin-right: 8px;
    }

    &:hover {
      // background: #F2F3F5;
      background: #fff;
    }

    &.is-speeding {
      .t-iconfont {
        color: #ff6a00 !important;
      }
    }
  }

  &__action {
    text-decoration: none;

    .el-icon-download {
      font-size: 16px;
      line-height: 1;
      color: #4e5969;
    }
  }

  &__speed {
    min-width: 40px;
  }

  &__progress-track {
    position: relative;
    width: 100%;
    height: 6px;
    overflow: hidden;
    background: rgba(24, 111, 210, 0.14);
    border-radius: 999px;
  }

  &__progress-fill {
    position: absolute;
    inset: 0 auto 0 0;
    background: #248df5;
    border-radius: inherit;
    transition: width 0.1s linear;
  }

  &__audio {
    display: none;
  }

  &.is-playing {
    // .voice-player__button {
    //   animation: voice-player-playing 0.6s infinite alternate;
    // }
  }
}

// @keyframes voice-player-playing {
//   0% {
//     opacity: 0.45;
//   }

//   100% {
//     opacity: 1;
//   }
// }
</style>

<style lang="scss">
.voice-player__speed-menu {
  min-width: 102px;
  padding: 4px 0;

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    height: 32px;
    padding: 6px 12px;
    font-size: 14px;
    line-height: 20px;
    color: #323233;

    &:hover,
    &:focus {
      background: #f2f3f5;
    }

    &.is-active {
      font-weight: 600;
      color: #ff6a00;
      background: #fff;
    }
  }
}
</style>
